import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { environment } from '../../../environments/environment';
import { AppError } from '../models/error.models';
import { devError, devGroup, devGroupEnd } from '@core';

/**
 * Payload sent to the logClientError Cloud Function (WI-018a).
 * Mirrors functions/src/callable/error-logging.callable.ts.
 */
export interface LogClientErrorPayload {
  message: string;
  category: string;
  severity: string;
  context: {
    userId?: string;
    route?: string;
    timestamp: number;
    appVersion: string;
    userAgent: string;
    additionalData?: Record<string, unknown>;
  };
  stackTrace?: string;
}

/**
 * Response from the logClientError Cloud Function.
 */
export interface LogClientErrorResponse {
  success: boolean;
  errorId?: string;
}

/**
 * Service for logging errors to Google Cloud Logging.
 *
 * Development: logs to the browser console (GDPR-compliant dev-only logging).
 * Production: forwards errors to the `logClientError` Cloud Function (WI-018a),
 * which authenticates, rate-limits, redacts PII, and writes structured logs to
 * Cloud Logging. Falls back to console.error only if the function call fails.
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorLoggerService {
  private readonly functions = inject(Functions);

  /**
   * Log error to the Cloud Function in production, console in development.
   */
  async logError(appError: AppError): Promise<void> {
    // Development: log to console only
    if (!environment.production) {
      this.logToConsole(appError);
      return;
    }

    // Production: forward to Cloud Function (WI-018a)
    try {
      const payload = this.buildPayload(appError);
      await this.sendToCloudFunction(payload);
    } catch (error: unknown) {
      // Fallback to console.error if the Cloud Function call fails.
      // Keep this minimal and PII-free (redacted message, no stack/context).
      console.error('[CheckerFly Error - Cloud Function failed]', {
        message: this.redactPII(appError.message),
        category: appError.category,
        severity: appError.severity,
        route: appError.context.route,
        functionError: error instanceof Error ? error.message : String(error)
      });
    }
  }

  /**
   * Build the Cloud Function payload from an AppError.
   * PII in message/stackTrace is redacted client-side (server redacts again).
   */
  private buildPayload(appError: AppError): LogClientErrorPayload {
    return {
      message: this.redactPII(appError.message),
      category: appError.category,
      severity: appError.severity,
      context: {
        userId: appError.context.userId,
        route: appError.context.route,
        timestamp: appError.context.timestamp,
        appVersion: appError.context.appVersion,
        userAgent: appError.context.userAgent,
        additionalData: appError.context.additionalData
      },
      stackTrace: appError.stackTrace
        ? this.redactPII(appError.stackTrace)
        : undefined
    };
  }

  /**
   * Invoke the logClientError Cloud Function.
   * Extracted for testability (spied on in unit tests).
   */
  private async sendToCloudFunction(
    payload: LogClientErrorPayload
  ): Promise<LogClientErrorResponse> {
    const callable = httpsCallable<LogClientErrorPayload, LogClientErrorResponse>(
      this.functions,
      'logClientError'
    );
    const result = await callable(payload);
    return result.data;
  }

  /**
   * Log error to browser console with formatting.
   * Uses devGroup/devGroupEnd for collapsible error blocks (dev-only, GDPR-compliant).
   */
  private logToConsole(appError: AppError): void {
    const { message, category, severity, context, stackTrace } = appError;

    // Use devGroup for collapsible error blocks (better DX)
    devGroup(`🚨 ${severity.toUpperCase()} Error [${category}]`);
    devError('Message:', message);
    devError('Context:', {
      userId: context.userId || 'anonymous',
      route: context.route || 'unknown',
      timestamp: new Date(context.timestamp).toISOString(),
      appVersion: context.appVersion
    });
    if (stackTrace) {
      devError('Stack Trace:', stackTrace);
    }
    if (context.additionalData) {
      devError('Additional Data:', context.additionalData);
    }
    devGroupEnd();
  }

  /**
   * Redact PII from stack traces and error messages.
   * Removes phone numbers, emails, and location coordinates.
   */
  private redactPII(text: string): string {
    return text
      // Redact phone numbers (E.164 format: +14155552671)
      .replace(/\+\d{10,15}/g, '[PHONE_REDACTED]')
      // Redact emails
      .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL_REDACTED]')
      // Redact GPS coordinates (lat,lng)
      .replace(/[-+]?\d{1,3}\.\d+,\s*[-+]?\d{1,3}\.\d+/g, '[LOCATION_REDACTED]');
  }
}
